import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Megaphone, Calendar, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "active", label: "Aktiv", cls: "bg-green-100 text-green-800" },
  { value: "paused", label: "Pausiert", cls: "bg-amber-100 text-amber-800" },
  { value: "completed", label: "Abgeschlossen", cls: "bg-gray-100 text-gray-700" },
  { value: "planned", label: "Geplant", cls: "bg-blue-100 text-blue-800" },
];

const emptyForm = { company_id: "", name: "", description: "", start_date: "", end_date: "", goals: [], status: "active" };

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [goalInput, setGoalInput] = useState("");
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [ca, co] = await Promise.all([
        axios.get(`${API}/campaigns`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setCampaigns(ca.data);
      setCompanies(co.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/campaigns/${editing}`, form, { withCredentials: true });
        toast.success("Kampagne aktualisiert");
      } else {
        await axios.post(`${API}/campaigns`, form, { withCredentials: true });
        toast.success("Kampagne erstellt");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error("Fehler"); }
  };

  const handleEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyForm, ...c });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Kampagne loeschen?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const addGoal = () => {
    if (goalInput.trim()) {
      setForm((f) => ({ ...f, goals: [...(f.goals || []), goalInput.trim()] }));
      setGoalInput("");
    }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  return (
    <div data-testid="campaigns-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("campaigns.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("campaigns.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-campaign-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-2" /> {t("campaigns.add_btn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Kampagne bearbeiten" : "Neue Kampagne"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Firma *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Firma waehlen" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Kampagnen-Name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="z.B. Fruehlingsputz 2026" />
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Beschreibung</Label>
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[var(--studio-text-muted)]">Start</Label>
                  <Input type="date" value={form.start_date || ""} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--studio-text-muted)]">Ende</Label>
                  <Input type="date" value={form.end_date || ""} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[var(--studio-text-muted)]">Ziele</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={goalInput} onChange={(e) => setGoalInput(e.target.value)} placeholder="z.B. +50% Reichweite"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGoal(); } }} />
                  <Button type="button" variant="outline" onClick={addGoal}>+</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.goals?.map((g, i) => (
                    <Badge key={i} variant="secondary" className="cursor-pointer hover:bg-red-100"
                      onClick={() => setForm((f) => ({ ...f, goals: f.goals.filter((_, idx) => idx !== i) }))}>{g} &times;</Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(emptyForm); }}>Abbrechen</Button>
              <Button onClick={handleSave} data-testid="save-campaign-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                disabled={!form.name || !form.company_id}>
                {editing ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <Megaphone className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">Noch keine Kampagnen erstellt.</p>
            <p className="text-sm text-[var(--studio-text-muted)] mt-1">Erstelle z.B. "Fruehlingsputz 2026" oder "Sommer-Challenge".</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((c) => {
            const st = statusOptions.find((s) => s.value === c.status) || statusOptions[0];
            return (
              <Card key={c.id} className="border-[var(--studio-border)] shadow-none hover:shadow-sm transition-shadow" data-testid={`campaign-card-${c.id}`}>
                <CardContent className="p-5 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold font-heading">{c.name}</h3>
                      <Badge className={`${st.cls} border-0 text-xs`}>{st.label}</Badge>
                    </div>
                    <p className="text-sm text-[var(--studio-text-muted)]">{companyName(c.company_id)}</p>
                    {c.description && <p className="text-sm mt-1">{c.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-[var(--studio-text-muted)]">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(c.start_date)} - {formatDate(c.end_date)}</span>
                      {c.content_count != null && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {c.content_count} Inhalte</span>}
                    </div>
                    {c.goals?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.goals.map((g, i) => <Badge key={i} variant="outline" className="text-xs">{g}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(c)} data-testid={`edit-campaign-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-campaign-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
